import { QueryClient, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

const CHANNEL_NAME = 'color-variant-classification-query-invalidations';

interface InvalidateMessage {
  type: 'invalidate';
  queryKey: QueryKey;
}

const isInvalidateMessage = (data: unknown): data is InvalidateMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === 'invalidate' &&
  Array.isArray((data as { queryKey?: unknown }).queryKey);

// Lazily-constructed singleton: BroadcastChannel is unsupported in some test/SSR environments
// (we feature-detect and silently no-op), and a per-tab singleton is required for echo
// suppression — BroadcastChannel only suppresses delivery to the *same* channel instance, so two
// channels created in the same tab would receive each other's messages and cause every local
// invalidation to also fire as a cross-tab message back into this same tab.
let cachedChannel: BroadcastChannel | null | undefined;

const getBroadcastChannel = (): BroadcastChannel | null => {
  if (cachedChannel !== undefined) return cachedChannel;
  if (typeof BroadcastChannel === 'undefined') {
    cachedChannel = null;
    return null;
  }
  try {
    cachedChannel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    cachedChannel = null;
  }
  return cachedChannel;
};

/**
 * Invalidate `queryKey` in the local React Query cache and broadcast the same invalidation to
 * other tabs running this app. Use as a drop-in replacement for `queryClient.invalidateQueries`
 * inside variant classification mutations so that, e.g., editing a classification in a Detail
 * View tab refreshes a List View open in another tab.
 *
 * BroadcastChannel does not echo to the sending channel instance, so the listener installed by
 * `useCrossTabQueryInvalidation` in this same tab will not re-invalidate locally.
 */
export const broadcastInvalidate = (queryClient: QueryClient, queryKey: QueryKey): void => {
  queryClient.invalidateQueries({ queryKey }).catch(() => {});
  const channel = getBroadcastChannel();
  if (channel === null) return;
  try {
    channel.postMessage({ type: 'invalidate', queryKey } satisfies InvalidateMessage);
  } catch {
    // Defensive: postMessage throws only on un-cloneable payloads, but we always send strings.
    // Failing to broadcast must never break the local mutation flow.
  }
};

/**
 * Subscribe the calling component's React Query cache to invalidation messages broadcast by
 * `broadcastInvalidate` running in other tabs. Mount once near the root of any view whose data
 * should stay in sync with sibling tabs (List View, Detail View).
 */
export const useCrossTabQueryInvalidation = (): void => {
  const queryClient = useQueryClient();
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    const channel = getBroadcastChannel();
    if (channel === null) return undefined;
    const handleMessage = (event: MessageEvent): void => {
      if (!isInvalidateMessage(event.data)) return;
      queryClient.invalidateQueries({ queryKey: event.data.queryKey }).catch(() => {});
    };
    channel.addEventListener('message', handleMessage);
    return () => channel.removeEventListener('message', handleMessage);
  }, [queryClient]);
};
